import { useState } from 'react'

import { Alert, Snackbar, Stack } from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTranslation } from 'react-i18next'
import { useLocation, useParams } from 'react-router-dom'

import { CardStack } from './cardStack'
import { ConfirmationDialog } from './confirmationDialog'
import { SignupDialog } from './signupDialog'
import { TableComponent as Table } from './table'
import { useProject, useWidgetConfig } from '../../api/widget'

export const ListWidget = () => {
  const { t } = useTranslation()
  const { projectId } = useParams()
  const location = useLocation()
  const [selectedProperty, setSelectedProperty] = useState(null)
  const [openSignup, setOpenSignup] = useState(false)
  const [openConfirmation, setOpenConfirmation] = useState(false)
  const [openSnackbar, setOpenSnackbar] = useState(false)

  const token = new URLSearchParams(location.search).get('token')

  const {
    data: project,
    isLoading: isLoadingProject,
    error: projectError,
  } = useProject(projectId, token)
  const {
    data: widgetConfig,
    isLoading: isLoadingConfig,
    error: configError,
  } = useWidgetConfig(projectId, token)

  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'))

  const properties = (project?.properties ?? []).map((p) => ({
    ...p,
    price: p.status === 'AVAILABLE' ? p.price : null,
    monthlyFee: p.status === 'AVAILABLE' ? p.monthlyFee : null,
  }))
  const isLoading = isLoadingProject || isLoadingConfig
  const error = projectError || configError

  const handleClickProperty = (property) => {
    setSelectedProperty(property)
    setOpenSignup(true)
  }

  const handleCloseSignup = () => {
    setSelectedProperty(null)
    setOpenSignup(false)
  }

  const handleSuccessSignup = () => {
    const redirectUrl = widgetConfig?.widgetRedirectUrl ?? ''
    if (redirectUrl) {
      window.location.href = redirectUrl
    } else {
      setOpenConfirmation(true)
      setOpenSignup(false)
      setSelectedProperty(null)
    }
  }

  const handleErrorSignup = () => {
    setOpenSnackbar(true)
  }

  const handleCloseConfirmation = () => {
    setOpenConfirmation(false)
  }

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false)
  }

  const hasTokenError = !token || error?.status === 401 || error?.status === 403

  if (hasTokenError) {
    return (
      <Alert severity="error">
        {t('Token is missing or invalid, please check the widget settings.')}
      </Alert>
    )
  }

  if (error) {
    return <Alert severity="error">{t('Something went wrong')}</Alert>
  }

  if (isLoading) {
    return null
  }

  return (
    <Stack>
      {isMobile ? (
        <CardStack
          fields={widgetConfig?.propertyVisibleFields}
          onClick={handleClickProperty}
          properties={properties}
        />
      ) : (
        <Table
          fields={widgetConfig?.propertyVisibleFields}
          onClick={handleClickProperty}
          properties={properties}
        />
      )}
      <SignupDialog
        fields={widgetConfig?.propertyVisibleFields}
        isOrg={project?.allowedBuyers?.[0] === 'COMPANY'}
        onClose={handleCloseSignup}
        onSuccess={handleSuccessSignup}
        onError={handleErrorSignup}
        open={openSignup}
        privacyPolicyHtml={widgetConfig?.privacyPolicyCheckboxLabelHtml}
        property={selectedProperty}
        token={token}
      />
      <ConfirmationDialog
        open={openConfirmation}
        onClose={handleCloseConfirmation}
      />
      <Snackbar
        open={openSnackbar}
        onClose={handleCloseSnackbar}
        message={t('Something went wrong')}
      />
    </Stack>
  )
}
